import {
  ChefHat, Calendar, User, Clock, CheckCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type {
  PrepList, PrepItemStatus 
} from '../../types';
import {
  PREP_LIST_STATUS_LABELS, PREP_LIST_STATUS_COLORS, MEAL_PERIOD_LABELS 
} from '../../types';

function PrepItemStatusIcon({ status }: { readonly status: PrepItemStatus }) {
  if (status === 'completed') {
    return <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />;
  }
  if (status === 'in-progress') {
    return <Clock className="h-3 w-3 text-amber-500 flex-shrink-0" />;
  }
  return <div className="h-3 w-3 rounded-full border border-muted-foreground flex-shrink-0" />;
}

interface PrepListCardProps {readonly prepList: PrepList;}

export function PrepListCard({ prepList }: PrepListCardProps) {
  const statusColor = PREP_LIST_STATUS_COLORS[prepList.status];
  const progress = prepList.totalItems > 0 ? (prepList.completedItems / prepList.totalItems) * 100 : 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ChefHat className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{prepList.venueName}</CardTitle>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
            {PREP_LIST_STATUS_LABELS[prepList.status]}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{MEAL_PERIOD_LABELS[prepList.mealPeriod]}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{new Date(prepList.date).toLocaleDateString()}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-4 w-4" />
          <span>Created by: {prepList.createdByName}</span>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{prepList.completedItems}/{prepList.totalItems} items</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted">
            <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Prep Items:</p>
          <div className="space-y-1">
            {prepList.items.slice(0, 4).map((item) => (
              <div key={item.itemId} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 flex-1 truncate">
                  <PrepItemStatusIcon status={item.status} />
                  <span className="truncate">{item.recipeName}</span>
                </div>
                <span className="text-muted-foreground ml-2 text-xs">
                  {item.completedQuantity}/{item.targetQuantity}
                </span>
              </div>
            ))}
            {prepList.items.length > 4 && (
              <p className="text-xs text-muted-foreground">+{prepList.items.length - 4} more items</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
